import {
  defaultLocale,
  localeMeta,
  supportedLocaleFor,
  supportedLocales,
  type SupportedLocale
} from "@tinkr/shared/locales";
import ar from "./i18n/ar";
import de from "./i18n/de";
import es from "./i18n/es";
import fr from "./i18n/fr";
import hi from "./i18n/hi";
import ko from "./i18n/ko";
import ms from "./i18n/ms";
import pt from "./i18n/pt";
import zhHans from "./i18n/zh-Hans";

export const appLocales = supportedLocales;
export type AppLocale = SupportedLocale;

export const appLocaleMeta = Object.fromEntries(
  supportedLocales.map((code) => [
    code,
    { label: localeMeta[code].nativeLabel, dir: localeMeta[code].dir }
  ])
) as Record<AppLocale, { label: string; dir: "ltr" | "rtl" }>;

const ja = {
  "success.pendingTitle": "ご注文の確認が続いています",
  "success.pendingBody": "お支払いの確認に通常より時間がかかっています。再度お支払いはしないでください。もう一度確認するか、ご注文に戻るか、以下の注文番号を添えてサポートにお問い合わせください。",
  "success.cancelledTitle": "このご注文はキャンセルされました",
  "success.cancelledBody": "カードへの請求や一時保留があった場合は、返金または解除の手続きを進めています。お客様の操作は必要ありません。",
  "success.checkAgain": "もう一度確認する",

  "error.countryClosed": "この国では新規注文と未払い注文の決済を終了しました。支払い済み注文とサポートは継続します。",
  "error.providerCountryClosed": "割り当てられたプロバイダーではこのご注文を完了できなくなりました。新しいご注文を開始いただければ、改めてマッチングします。",
  "chrome.reviewTitle": "お支払い内容の確認",
  "chrome.paidTitle": "ご注文の確認",
  "chrome.paymentTitle": "お支払い",
  "chrome.language": "言語",
  "chrome.close": "閉じる",
  "chrome.logoAlt": "Tinkr",

  "common.loading": "読み込み中",
  "common.error": "問題が発生しました。",
  "common.retry": "もう一度試す",
  "common.labelSeparator": "：",

  "trust.badge": "販売：Tinkr ・ 製造：認定パートナー",

  "review.orderSummary": "ご注文内容",
  "review.threeD": "3D",
  "review.specsQty": "数量",
  "review.delivery.title": "お届け予定",
  "review.shipping.title": "お届け先",
  "review.shipping.edit": "編集",
  "review.payment.title": "お支払い方法",
  "review.payment.card": "クレジット / デビットカード",
  "review.payment.cardHint": "Visa・Mastercard ほか主要ブランド",
  "review.payment.usdc": "USDC ステーブルコイン",
  "review.payment.comingSoon": "近日対応",
  "review.price.title": "料金",
  "review.price.itemSubtotal": "商品小計",
  "review.price.premium": "プレミアム製作",
  "review.price.shipping": "送料",
  "review.price.shippingIncluded": "料金に含まれています",
  "review.price.total": "ご請求金額",
  "review.price.taxNote": "この注文について、Tinkr は取引税を請求していません。",
  "review.price.updating": "合計金額を更新しています…",
  "review.price.refreshError": "合計金額を更新できませんでした。お支払いの前にページを再読み込みしてください。",
  "review.price.multicolor": "マルチカラー",
  "review.price.multicolorCapNote": "このフィギュアを正確に計測できなかったため、マルチカラーの上限価格が適用されます。",
  "review.pricing.calculating": "正確な価格を計算しています…",
  "review.pricing.calculatingBody": "マルチカラーのフィギュアは、その造形に合わせて価格を計算します。通常は1分以内に完了します。",
  "review.pricing.from": "最低価格",
  "review.pricing.stillWorking": "引き続き計算しています。もう少しお待ちください。",
  "review.pricing.timeout": "価格の計算を完了できませんでした。もう一度お試しください。",
  "review.consent.prefix": "下記に同意します：",
  "review.consent.terms": "利用規約",
  "review.consent.and": "・",
  "review.consent.privacy": "プライバシーポリシー",
  "review.cta.pay": "お支払い",
  "review.cta.secure": "安全な決済",

  "coupon.toggle": "クーポンをお持ちですか？",
  "coupon.label": "クーポンコード",
  "coupon.placeholder": "コードを入力",
  "coupon.apply": "適用する",
  "coupon.applying": "適用中…",
  "coupon.remove": "削除",
  "coupon.removing": "削除中…",
  "coupon.appliedLabel": "クーポン",
  "coupon.error.notFound": "このクーポンコードは見つかりません",
  "coupon.error.expired": "このクーポンの有効期限が切れています",
  "coupon.error.revoked": "このクーポンは無効になっています",
  "coupon.error.notStarted": "このクーポンはまだご利用いただけません",
  "coupon.error.notApplicable": "このクーポンはこのご注文には使えません",
  "coupon.error.usageLimit": "このクーポンは利用上限に達しました",
  "coupon.error.alreadyApplied": "このクーポンはすでに適用されています",
  "coupon.error.retry": "時間をおいてもう一度お試しください",
  "coupon.error.generic": "このコードはご利用いただけません",

  "redirect.title": "安全なお支払い画面へ移動しています…",
  "redirect.subtitle": "Stripe の安全な決済ページへリダイレクトしています。",
  "redirect.badgeVerified": "本人確認",
  "redirect.badgeEncrypted": "暗号化",
  "redirect.badgeSsl": "SSL 保護接続",

  "success.title": "お支払いが完了しました！",
  "success.authorizedTitle": "ご注文を確認しました！",
  "success.orderNumber": "注文番号",
  "success.paidTotal": "支払い合計",
  "success.authorizedTotal": "カードの一時保留額",
  "success.nextTitle": "今後の流れ",
  "success.step1": "パートナーへ通知済み",
  "success.step2": "製造・検品",
  "success.step3": "発送",
  "success.statusCompleted": "完了",
  "success.statusPending": "進行中",
  "success.statusUpcoming": "これから",
  "success.track": "注文を追跡する",
  "success.viewOrder": "注文を確認する →",
  "success.back": "トップに戻る",
  "success.returnToDoodles": "Tinkr Doodlesに戻る",
  "success.returnToSite": "注文画面に戻る",
  "success.confirming": "ご注文を確認しています…",
  "success.notConfirmed": "ご注文をまだ確認できませんでした。数分後にもう一度お試しください。",

  "cancelled.title": "決済画面を離れました",
  "cancelled.body": "注文内容は保存されています。お支払い状況の更新には少し時間がかかる場合があります。",
  "cancelled.orderRef": "注文番号",
  "cancelled.tip": "接続環境やカード会社の認証をご確認のうえ、もう一度お試しください。",
  "cancelled.retry": "もう一度試す",
  "cancelled.help": "ヘルプセンター",

  "footer.terms": "利用規約",
  "footer.privacy": "プライバシー",
  "footer.refund": "返品・キャンセル",
  "footer.seller": "販売者情報",
  "footer.tokushoho": "特定商取引法に基づく表記",
  "footer.entity": "Tinkr App Ltd",
  "footer.companyNo": "会社番号",

  "legal.back": "戻る",
  "legal.updated": "最終更新",

  "error.notFound": "このお支払いリンクは見つかりませんでした。メールのリンクをご確認ください。",
  "error.expired": "このお支払いリンクは有効期限が切れています。",
  "error.alreadyPaid": "このご注文はすでにお支払い済みです。",
  "error.payFailed": "決済を開始できませんでした。もう一度お試しください。",
  "error.couponNoLongerValid":
    "適用中のクーポンが無効になりました。クーポンを削除してからもう一度お試しください。",
  "error.couponLimitReached":
    "このクーポンは現在、利用上限に達しています。クーポンを削除して続行するか、しばらくしてからもう一度お試しください。",
  "error.couponNotApplicable": "このクーポンはこのご注文金額にはご利用いただけません。クーポンを削除してからもう一度お試しください。",
  "error.paymentConfirming": "お支払いはすでに確認中です。少し待ってからこのページを再読み込みしてください。",
  "error.sessionConflict": "別のウィンドウでチェックアウトが変更されました。もう一度お試しください。",
  "error.recoveryHint":
    "ご注文に関する最新のメールを開くと、新しいお支払いリンクをご利用いただけます。お困りの場合はサポートまでご連絡ください。",
  "error.backToSite": "サイトに戻る"
} as const;

const en: Record<keyof typeof ja, string> = {
  "success.pendingTitle": "Your order is still being confirmed",
  "success.pendingBody": "Payment confirmation is taking longer than usual. Please do not pay again. You can check again, return to your order, or contact support with the order number below.",
  "success.cancelledTitle": "This order was cancelled",
  "success.cancelledBody": "If your card was charged or held, the money is being returned to it. You do not need to do anything.",
  "success.checkAgain": "Check again",
  "error.countryClosed": "New orders and unpaid checkouts are no longer available in this country. Paid orders and support continue.",
  "error.providerCountryClosed": "Your order can no longer be fulfilled by the assigned provider. Start a new order and we will match you again.",
  "chrome.reviewTitle": "Review & Pay",
  "chrome.paidTitle": "Order confirmation",
  "chrome.paymentTitle": "Payment",
  "chrome.language": "Language",
  "chrome.close": "Close",
  "chrome.logoAlt": "Tinkr",

  "common.loading": "Loading",
  "common.error": "Something went wrong.",
  "common.retry": "Try again",
  "common.labelSeparator": ": ",

  "trust.badge": "Sold by Tinkr · Produced by an approved local print partner",

  "review.orderSummary": "Order summary",
  "review.threeD": "3D",
  "review.specsQty": "Qty",
  "review.delivery.title": "Delivery",
  "review.shipping.title": "Shipping details",
  "review.shipping.edit": "Edit",
  "review.payment.title": "Payment method",
  "review.payment.card": "Credit / Debit card",
  "review.payment.cardHint": "Visa, Mastercard and more",
  "review.payment.usdc": "USDC Stablecoin",
  "review.payment.comingSoon": "Coming soon",
  "review.price.title": "Price",
  "review.price.itemSubtotal": "Item subtotal",
  "review.price.premium": "Premium fulfilment",
  "review.price.shipping": "Shipping",
  "review.price.shippingIncluded": "Included",
  "review.price.total": "Order total",
  "review.price.taxNote": "Transaction tax is not charged by Tinkr for this order.",
  "review.price.updating": "Updating your total…",
  "review.price.refreshError": "We couldn't refresh your total. Reload the page before paying.",
  "review.price.multicolor": "Multicolor",
  "review.price.multicolorCapNote": "We couldn't measure this figurine exactly, so the maximum multicolor price applies.",
  "review.pricing.calculating": "Calculating your exact price…",
  "review.pricing.calculatingBody": "Your multicolor figurine is priced from its own print. This usually takes under a minute.",
  "review.pricing.from": "Starting from",
  "review.pricing.stillWorking": "Still working. Thanks for your patience.",
  "review.pricing.timeout": "We couldn't finish calculating your price. Please try again.",
  "review.consent.prefix": "By checking this, you agree to Tinkr's",
  "review.consent.terms": "Terms of Service",
  "review.consent.and": "and",
  "review.consent.privacy": "Privacy Policy",
  "review.cta.pay": "Pay",
  "review.cta.secure": "Secure checkout",

  "coupon.toggle": "Have a coupon code?",
  "coupon.label": "Coupon code",
  "coupon.placeholder": "Enter code",
  "coupon.apply": "Apply",
  "coupon.applying": "Applying…",
  "coupon.remove": "Remove",
  "coupon.removing": "Removing…",
  "coupon.appliedLabel": "Coupon",
  "coupon.error.notFound": "We couldn't find that coupon code",
  "coupon.error.expired": "This coupon has expired",
  "coupon.error.revoked": "This coupon is no longer valid",
  "coupon.error.notStarted": "This coupon isn't active yet",
  "coupon.error.notApplicable": "This coupon can't be used for this order",
  "coupon.error.usageLimit": "This coupon has reached its usage limit",
  "coupon.error.alreadyApplied": "This coupon is already applied",
  "coupon.error.retry": "Please wait a moment and try again",
  "coupon.error.generic": "This code can't be used",

  "redirect.title": "Taking you to secure payment…",
  "redirect.subtitle": "Redirecting you to Stripe's secure payment page.",
  "redirect.badgeVerified": "Verified",
  "redirect.badgeEncrypted": "Encrypted",
  "redirect.badgeSsl": "SSL Secured Connection",

  "success.title": "Payment complete!",
  "success.authorizedTitle": "Order confirmed!",
  "success.orderNumber": "Order number",
  "success.paidTotal": "Amount paid",
  "success.authorizedTotal": "Temporary card hold",
  "success.nextTitle": "What happens next",
  "success.step1": "Your print partner is notified",
  "success.step2": "They print & quality check",
  "success.step3": "It ships to you",
  "success.statusCompleted": "Completed",
  "success.statusPending": "In progress",
  "success.statusUpcoming": "Upcoming",
  "success.track": "Track your order",
  "success.viewOrder": "View your order →",
  "success.back": "Back to Tinkr",
  "success.returnToDoodles": "Return to Tinkr Doodles",
  "success.returnToSite": "Return to your order",
  "success.confirming": "Confirming your order…",
  "success.notConfirmed": "We couldn't confirm your order yet. Please check again in a few minutes.",

  "cancelled.title": "You left checkout",
  "cancelled.body": "Your order is saved. Payment status can take a moment to update.",
  "cancelled.orderRef": "Order number",
  "cancelled.tip": "Check your connection or bank authorization and try again.",
  "cancelled.retry": "Try again",
  "cancelled.help": "Help",

  "footer.terms": "Terms of Service",
  "footer.privacy": "Privacy",
  "footer.refund": "Refunds & cancellations",
  "footer.seller": "Seller information",
  "footer.tokushoho": "Specified Commercial Transactions Act notice",
  "footer.entity": "Tinkr App Ltd",
  "footer.companyNo": "Company No.",

  "legal.back": "Back",
  "legal.updated": "Last updated",

  "error.notFound": "We couldn't find this checkout link. Check the link from your email.",
  "error.expired": "This checkout link has expired.",
  "error.alreadyPaid": "This order has already been paid.",
  "error.payFailed": "We couldn't start the payment. Please try again.",
  "error.couponNoLongerValid": "The coupon on this order is no longer valid. Remove it and try again.",
  "error.couponLimitReached": "This coupon has reached its usage limit for now. Remove it to continue, or try again later.",
  "error.couponNotApplicable": "This coupon can't be used with this order total. Remove it and try again.",
  "error.paymentConfirming": "Your payment is already being confirmed. Wait a moment, then refresh this page.",
  "error.sessionConflict": "Checkout changed in another window. Please try again.",
  "error.recoveryHint":
    "Open the latest email about your order for a fresh payment link, or contact support and we'll sort it out.",
  "error.backToSite": "Back to the site"
};

export type MessageKey = keyof typeof ja;
export type Dictionary = Record<MessageKey, string>;
export type Translate = (key: MessageKey) => string;
export const messageKeys = Object.keys(en) as MessageKey[];

const dictionaries: Record<AppLocale, Dictionary> = {
  en,
  es,
  fr,
  de,
  pt,
  "zh-Hans": zhHans,
  ja,
  ko,
  ar,
  hi,
  ms
};

export function translate(locale: AppLocale, key: MessageKey): string {
  return dictionaries[locale][key];
}

const STORAGE_KEY = "tinkr-checkout-locale";

export function detectLocale(preferred: readonly string[]): AppLocale {
  for (const raw of preferred) {
    const value = raw.trim();
    if (!value) continue;
    const normalized = value.toLowerCase();
    const isRecognised = supportedLocales.some((candidate) => {
      const canonical = candidate.toLowerCase();
      return normalized === canonical || normalized.startsWith(`${canonical}-`);
    }) || normalized === "zh" || normalized.startsWith("zh-");
    if (isRecognised) return supportedLocaleFor(value);
  }
  return defaultLocale;
}

export function detectInitialLocale(): AppLocale {
  if (typeof window === "undefined") return defaultLocale;
  const params = new URLSearchParams(window.location.search);
  const langParam = params.get("lang");
  if (langParam) return detectLocale([langParam]);
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    stored = null;
  }
  return detectLocale([stored ?? "", ...window.navigator.languages]);
}

export function persistLocale(locale: AppLocale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Storage unavailable (private mode); selection still applies for the session.
  }
}

/**
 * Reflect the active locale and writing direction on the root element before
 * first paint and after every language switch.
 */
export function applyDocumentLocale(locale: AppLocale): void {
  document.documentElement.lang = locale;
  document.documentElement.dir = appLocaleMeta[locale].dir;
}
